// src/utils/globalFormatters.ts
import { useTranslation } from 'react-i18next';
import { LOCALES, NUMBER_FORMATS } from '@/utils/constants';
import currencies from '@/pages/personal/settings/SettingsSection/currencies.json';

// Переиспользуем функции из dateUtils.ts
import {
  utcToLocal,
  localToUtc,
  formatDateTime,
  formatDateForInput,
  getUtcDateIsoString,
  isToday,
  isYesterday,
  formatSmartDate,
  formatTime
} from '@/utils/dateUtils';

// Типы данных для валют
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  translation_key: string;
}

// Используем данные из импортированного JSON файла
const currenciesList: Currency[] = currencies;

// Создаем объект с символами валют для быстрого доступа
const currencySymbols: Record<string, string> = currenciesList.reduce((acc, curr) => {
  acc[curr.code] = curr.symbol;
  return acc;
}, {} as Record<string, string>);

// ====================== ХУКИ ДЛЯ РАБОТЫ С ДАТАМИ ======================

/**
 * Хук для форматирования даты с использованием i18n
 */
export const useFormattedDate = () => {
  const { i18n } = useTranslation();
  
  const formatDate = (date: string | null, includeTime: boolean = false): string => {
    return formatDateTime(date, includeTime, i18n.language);
  };
  
  return { formatDate };
};

// Реэкспортируем все функции для работы с датами для обратной совместимости
export {
  utcToLocal,
  localToUtc,
  formatDateTime as formatDate,
  formatDateForInput,
  getUtcDateIsoString,
  isToday,
  isYesterday,
  formatSmartDate,
  formatTime
};

// ====================== ФОРМАТИРОВАНИЕ ЧИСЕЛ ======================

/**
 * Форматирует числовое значение с указанной точностью
 * @param value - значение для форматирования
 * @param precision - количество десятичных знаков
 * @param useLocale - использовать локализованный формат
 * @returns форматированная строка
 */
export const formatNumber = (
  value: number, 
  precision: number = NUMBER_FORMATS.DEFAULT_PRECISION, 
  useLocale: boolean = true,
  locale: string = LOCALES.RU
): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return '—';
  }
  
  // Округляем число до указанной точности
  const roundedValue = Number(value.toFixed(precision));
  
  // Если нужен локализованный формат
  if (useLocale) {
    return roundedValue.toLocaleString(locale, {
      minimumFractionDigits: precision === 0 ? 0 : Math.min(precision, 2),
      maximumFractionDigits: precision
    });
  }
  
  // Простое форматирование с заданной точностью
  return roundedValue.toString();
};

/**
 * Форматирует процентное значение
 * @param value - значение для форматирования (0-100 или 0-1)
 * @param precision - количество десятичных знаков
 * @param includeSymbol - добавлять символ %
 * @returns форматированная строка
 */
export const formatPercent = (
  value: number, 
  precision: number = NUMBER_FORMATS.PERCENT_PRECISION, 
  includeSymbol: boolean = true,
  locale: string = LOCALES.RU
): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return '—';
  }
  
  // Если значение в формате 0-1, преобразуем в проценты (0-100)
  const percentValue = value > 1 ? value : value * 100;
  
  // Округляем до указанной точности
  const formatted = Number(percentValue.toFixed(precision)).toLocaleString(locale, {
    minimumFractionDigits: precision === 0 ? 0 : Math.min(precision, 1),
    maximumFractionDigits: precision
  });
  
  return includeSymbol ? `${formatted}%` : formatted;
};

/**
 * Форматирует денежное значение с фиксированной точностью
 * @param amount - значение для форматирования
 * @param locale - локаль
 * @param currency - валюта
 * @param precision - количество десятичных знаков
 * @returns форматированная строка
 */
export const formatCurrency = (
  amount: string | number, 
  locale: string = LOCALES.RU, 
  currency: string = currencySymbols.KZT,
  precision: number = NUMBER_FORMATS.CURRENCY_PRECISION
): string => {
  try {
    const value = typeof amount === 'string' 
      ? parseFloat(amount.replace(/[^\d.-]/g, ''))
      : amount;
      
    if (isNaN(value)) return '0 ' + currency;
    
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(value) + ' ' + currency;
  } catch (error) {
    return '0 ' + currency;
  }
};

/**
 * Форматирует денежное значение с адаптивной точностью
 * в зависимости от величины числа
 * @param amount - значение для форматирования
 * @param currencySymbol - символ валюты
 * @param locale - локаль
 * @param maxFractionDigits - максимальное количество знаков после запятой
 * @returns форматированная строка
 */
export const formatPrice = (
  amount: number | string,
  currencySymbol: string = currencySymbols.KZT,
  locale: string = LOCALES.RU,
  maxFractionDigits?: number
): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d.-]/g, '')) : amount;
  
  if (isNaN(numericAmount)) return `0 ${currencySymbol}`;
  
  // Определение количества десятичных знаков в зависимости от величины числа
  const getAdaptiveMaxFractionDigits = (num: number): number => {
    const absNum = Math.abs(num);
    if (absNum >= 1000) return 0;  // Для сумм >= 1000 округляем до целых
    if (absNum >= 100) return 1;   // Для сумм >= 100 показываем 1 знак после запятой
    return 2;                      // Для меньших сумм показываем до 2 знаков
  };
  
  // Используем переданное значение или вычисляем автоматически
  const fractionDigits = maxFractionDigits !== undefined
    ? maxFractionDigits
    : getAdaptiveMaxFractionDigits(numericAmount);
  
  // Форматируем число с учетом локали и добавляем символ валюты
  const formattedNumber = numericAmount.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits === 0 ? 0 : 1, // Минимум 1 знак, если не нулевые дроби
    maximumFractionDigits: fractionDigits  // Динамическое количество знаков
  });
  
  return `${formattedNumber} ${currencySymbol}`;
};

/**
 * Сокращает большие числа для удобочитаемости
 * @param value - значение для форматирования
 * @param precision - количество десятичных знаков
 * @returns сокращенное значение (1.2K, 5.3M и т.д.)
 */
export const formatCompactNumber = (
  value: number, 
  precision: number = 1
): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return '—';
  }
  
  const abs = Math.abs(value);
  
  if (abs >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(precision)}B`;
  } 
  
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(precision)}M`;
  }
  
  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(precision)}K`;
  }
  
  return value.toFixed(precision);
};

// ====================== ПРОЧИЕ ФОРМАТТЕРЫ ======================

/**
 * Форматирование телефонного номера
 */
export const formatPhoneNumber = (phone: string): string => {
  try {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/);
    
    if (match) {
      return `+${match[1]} ${match[2]} ${match[3]} ${match[4]} ${match[5]}`;
    }
    
    return phone;
  } catch (error) {
    return phone;
  }
};

/**
 * Форматирует ФИО: Иванов И.И.
 */
export const formatShortName = (fullName: string): string => {
  if (!fullName) return '';
  
  try {
    const parts = fullName.trim().split(' ');
    if (parts.length < 2) return fullName;
    
    const lastName = parts[0];
    const initials = parts.slice(1).map(name => name.charAt(0).toUpperCase() + '.').join('');
    
    return `${lastName} ${initials}`;
  } catch (error) {
    return fullName;
  }
};

/**
 * Обрезает текст до определенной длины и добавляет многоточие
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};

// ====================== ДОПОЛНИТЕЛЬНЫЕ ФУНКЦИИ ИЗ formatters.ts ======================

/**
 * Форматирование процентов (упрощенная версия)
 */
export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

/**
 * Форматирование периода в днях с поддержкой i18n
 * @param days - количество дней
 * @param t - функция перевода из i18n
 */
export const formatPeriod = (days: number, t: (key: string, options?: any) => string): string => {
  if (days === 0) return '—';
  return t('common.days_count', { count: days });
};

/**
 * Форматирование адреса с поддержкой i18n
 */
export const formatAddress = (
  street: string,
  house: string,
  t: (key: string) => string,
  entrance?: string,
  floor?: string,
  apartment?: string
): string => {
  let address = `${street}, ${t('address.house')} ${house}`;
  
  if (entrance) address += `, ${t('address.entrance')} ${entrance}`;
  if (floor) address += `, ${t('address.floor')} ${floor}`;
  if (apartment) address += `, ${t('address.apartment')} ${apartment}`;
  
  return address;
};

/**
 * Форматирование времени для отображения временных промежутков с поддержкой i18n
 */
export const formatTimeAgo = (date: string, t: (key: string, options?: any) => string): string => {
  // Здесь нам нужны реальные временные расчеты, 
  // но мы можем минимизировать влияние часовых поясов, сравнивая разницу в минутах
  try {
    const now = new Date();
    // Парсим дату без изменения часового пояса
    const parts = date.split('T');
    if (parts.length < 2) return formatDateTime(date, false);
    
    const datePart = parts[0].split('-');
    const timePart = parts[1].match(/^(\d{2}):(\d{2}):(\d{2})/);
    
    if (datePart.length !== 3 || !timePart) return formatDateTime(date, false);
    
    const year = parseInt(datePart[0]);
    const month = parseInt(datePart[1]) - 1;
    const day = parseInt(datePart[2]);
    const hour = parseInt(timePart[1]);
    const minute = parseInt(timePart[2]);
    const second = parseInt(timePart[3]);
    
    // Учитываем возможное смещение часового пояса
    const tzMatch = parts[1].match(/([+-])(\d{2}):?(\d{2})$/);
    let tzOffsetMinutes = 0;
    
    if (tzMatch) {
      const tzHours = parseInt(tzMatch[2]);
      const tzMinutes = parseInt(tzMatch[3]);
      tzOffsetMinutes = (tzHours * 60 + tzMinutes) * (tzMatch[1] === '+' ? -1 : 1);
    }
    
    // Создаем дату в UTC
    const past = new Date(Date.UTC(year, month, day, hour, minute, second));
    // Применяем смещение часового пояса, если оно указано
    if (tzOffsetMinutes !== 0) {
      past.setMinutes(past.getMinutes() + tzOffsetMinutes);
    }
    
    const diffInMinutes = Math.floor((now.getTime() - past.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('time.just_now');
    if (diffInMinutes < 60) return t('time.minutes_ago', { count: diffInMinutes });
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('time.hours_ago', { count: diffInHours });
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('time.days_ago', { count: diffInDays });
    
    return formatDateTime(date, false);
  } catch (error) {
    return formatDateTime(date, false);
  }
};

/**
 * Форматирование размера файла с поддержкой i18n
 */
export const formatFileSize = (bytes: number, t: (key: string) => string): string => {
  if (bytes === 0) return t('files.zero_bytes');

  const k = 1024;
  const sizes = ['files.bytes', 'files.kb', 'files.mb', 'files.gb', 'files.tb'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + t(sizes[i]);
};